// Advisory, read-only Book Workspace progress labels. These are not publication
// approval, manuscript completion, snapshot, or export checkpoints.
// A pre-created Chapter 1 is structure, not evidence that writing has started.
const clean = value => String(value ?? "").trim();
const coreFields = ["trimSize", "orientation", "bleed", "publicationFormat"];

const savedChoice = (format, field) => {
  const value = clean(format?.[field]).toLowerCase();
  if (!value || ["not_decided", "undecided", "not_set", "none"].includes(value)) return false;
  if (field === "trimSize" && value === "custom_dimensions") {
    return Boolean(clean(format?.customTrimSize));
  }
  return true;
};

export function novelBookWorkspaceProgressV1({
  bookFormat = {},
  frontCoverUrl = "",
  backCoverUrl = "",
  chapterCount = 0,
  sceneCount = 0,
  wordCount = 0,
  publicationStatus = "inDevelopment",
  isFR = false,
} = {}) {
  const format = bookFormat && typeof bookFormat === "object" && !Array.isArray(bookFormat)
    ? bookFormat : {};
  const formatChoices = coreFields.filter(field => savedChoice(format, field)).length;
  const coverCount = [frontCoverUrl, backCoverUrl].filter(value => clean(value)).length;
  const chapters = Math.max(0, Number(chapterCount) || 0);
  const scenes = Math.max(0, Number(sceneCount) || 0);
  const words = Math.max(0, Number(wordCount) || 0);
  const finished = ["completed", "publishedamazon", "publishedexternal"]
    .includes(clean(publicationStatus).toLowerCase());
  const noScenes = scenes === 0;
  // Only suggest Book Setup before the first scene; never stop a writer who has
  // started writing, and never override the separate owner/pilot checkpoints.
  const suggestSetupFirst = chapters > 0 && noScenes && formatChoices < coreFields.length && !finished;

  return {
    formatChoices,
    coverCount,
    suggestSetupFirst,
    setupStatus: isFR
      ? `Format ${formatChoices}/4 · couvertures ${coverCount}/2`
      : `Format ${formatChoices}/4 · covers ${coverCount}/2`,
    structureStatus: isFR
      ? `${chapters} ${chapters === 1 ? "chapitre" : "chapitres"} · ${scenes} ${scenes === 1 ? "scène" : "scènes"}`
      : `${chapters} ${chapters === 1 ? "chapter" : "chapters"} · ${scenes} ${scenes === 1 ? "scene" : "scenes"}`,
    writeStatus: noScenes
      ? (isFR ? "Pas encore commencé" : "Not started")
      : isFR
      ? `${scenes} ${scenes === 1 ? "scène" : "scènes"} · ${words} mots`
      : `${scenes} ${scenes === 1 ? "scene" : "scenes"} · ${words} words`,
    reviewStatus: noScenes
      ? (isFR ? "Aucune scène à réviser" : "No scenes to review")
      : (isFR ? "Révision non confirmée" : "Review not confirmed"),
    preparationStatus: noScenes
      ? (isFR ? "Manuscrit pas commencé" : "Manuscript not started")
      : (isFR ? "Vérifications non confirmées" : "Checks not confirmed"),
    layoutStatus: noScenes
      ? (isFR ? "Aperçu provisoire · sans scène" : "Provisional · no scenes")
      : (isFR ? "Aperçu provisoire seulement" : "Provisional preview only"),
    summary: isFR
      ? `Progrès : ${chapters} ${chapters === 1 ? "chapitre" : "chapitres"} · ${scenes} ${scenes === 1 ? "scène" : "scènes"} · ${words} mots`
      : `Progress: ${chapters} ${chapters === 1 ? "chapter" : "chapters"} · ${scenes} ${scenes === 1 ? "scene" : "scenes"} · ${words} words`,
    explanation: isFR
      ? "Les choix de format n’empêchent jamais d’écrire. Un chapitre vide ne signifie pas que la rédaction ou la révision est terminée."
      : "Format choices never block writing. An empty chapter does not mean writing or review is complete.",
    setupNextDetail: isFR
      ? `Format ${formatChoices}/4 renseigné. Vous pouvez commencer à écrire maintenant ou plus tard.`
      : `Format ${formatChoices}/4 selected. You can start writing before finishing setup.`,
  };
}
